/**
 * Detect a deliberate horizontal swipe on a touch device.
 *
 * Once the drag reads as horizontal it calls `preventDefault()` on
 * touchmove, which both stops the page scrolling sideways and stops the
 * browser's own edge back/forward gesture from stealing the swipe. Vertical
 * drags (scrolling), multi-touch and slow drags are ignored.
 *
 *   onSwipe(node, { onLeft, onRight })  ->  cleanup fn
 */
export function onSwipe(target, { onLeft, onRight, threshold = 60 } = {}) {
  let x0 = 0;
  let y0 = 0;
  let t0 = 0;
  let active = false;
  let claimed = false;
  let startNode = null;

  // Would a horizontal drag from `node` scroll a nested element instead of
  // the page? (moves list, tab strip, coverage grid…) `dir` < 0 means the
  // finger is moving left — that scrolls such an element to the right.
  const scrollsInside = (node, dir) => {
    for (
      let n = node;
      n && n !== document.body && n !== target.parentElement;
      n = n.parentElement
    ) {
      if (n.scrollWidth - n.clientWidth > 2) {
        const ox = getComputedStyle(n).overflowX;
        if (ox === 'auto' || ox === 'scroll') {
          const max = n.scrollWidth - n.clientWidth;
          if (dir < 0 && n.scrollLeft < max - 1) return true;
          if (dir > 0 && n.scrollLeft > 1) return true;
        }
      }
    }
    return false;
  };

  const start = (e) => {
    if (e.touches.length !== 1) {
      active = false;
      return;
    }
    x0 = e.touches[0].clientX;
    y0 = e.touches[0].clientY;
    t0 = Date.now();
    active = true;
    claimed = false;
    startNode = e.target;
  };

  const move = (e) => {
    if (!active || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - x0;
    const dy = e.touches[0].clientY - y0;
    if (!claimed) {
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      // Scroll is the default: bail out — permanently — at the first sign of
      // vertical intent, even a mildly diagonal one. A page full of scrolling
      // content (tab panels, lists) means a fast flick's first sample is
      // often diagonal; without this bias it gets misread as a page-swipe
      // and the rest of the scroll gets eaten by preventDefault().
      if (ady > 10 && ady > adx * 0.7) {
        active = false;
        return;
      }
      // Only claim a horizontal page-swipe once the drag is unambiguous —
      // and not when it should scroll something nested instead.
      if (adx > 20 && adx > ady * 2) {
        if (scrollsInside(startNode, dx)) {
          active = false;
          return;
        }
        claimed = true;
      }
    }
    if (claimed) e.preventDefault();
  };

  const end = (e) => {
    if (!active) return;
    active = false;
    if (!claimed) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - x0;
    if (Date.now() - t0 > 700 || Math.abs(dx) < threshold) return;
    (dx < 0 ? onLeft : onRight)?.();
  };

  target.addEventListener('touchstart', start, { passive: true });
  target.addEventListener('touchmove', move, { passive: false });
  target.addEventListener('touchend', end, { passive: true });
  return () => {
    target.removeEventListener('touchstart', start);
    target.removeEventListener('touchmove', move);
    target.removeEventListener('touchend', end);
  };
}
